import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar, Clock, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { AuthContext } from '../App';
import { formatGusmesterNames } from '../utils/gusmesterDisplay';

interface Session {
  id: number;
  name: string;
  description: string;
  session_date: string;
  start_time: string;
  end_time: string;
  gusmester_name: string;
  gusmester_email: string;
  max_capacity: number;
  booked_count: number;
  waiting_count: number;
  session_type: 'regular' | 'special_event';
  gusmesters?: { id: number; name: string; email: string }[];
}

interface MonthlyBookingCount {
  count: number;
  limit: number;
}

const Booking: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [session, setSession] = useState<Session | null>(null);
  const [monthlyBookings, setMonthlyBookings] = useState<MonthlyBookingCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [joiningWaitingList, setJoiningWaitingList] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);

  useEffect(() => {
    if (session) {
      fetchMonthlyBookings();
    }
  }, [session, sessionId]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`/api/sessions/${sessionId}`);
      setSession(response.data);
    } catch (error) {
      setError('Kunne ikke indlæse session detaljer');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyBookings = async () => {
    if (!sessionId) return;
    
    try {
      const sessionDate = session ? new Date(session.session_date) : new Date();
      const year = sessionDate.getFullYear();
      const month = (sessionDate.getMonth() + 1).toString().padStart(2, '0');
      const response = await axios.get(`/api/user/monthly-bookings/${year}/${month}`);
      setMonthlyBookings(response.data);
    } catch (error) {
      console.error('Kunne ikke hente månedlige bookinger:', error);
    }
  };

  const handleBooking = async () => {
    if (!sessionId) {
      setError('Session ID er påkrævet');
      return;
    }

    setBookingLoading(true);
    setError('');

    try {
      await axios.post('/api/bookings', {
        sessionId: parseInt(sessionId)
      });
      
      setSuccess(true);
      await fetchMonthlyBookings();
      setTimeout(() => {
        navigate('/my-bookings');
      }, 3000);
    } catch (error: any) {
      if (error.response?.status === 409 && error.response?.data?.sessionFull) {
        // Session is full, offer waiting list
        setError('Session er fuldt booket. Vil du tilslutte dig ventelisten?');
      } else {
        setError(error.response?.data?.error || 'Booking mislykkedes. Prøv venligst igen.');
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const handleJoinWaitingList = async () => {
    if (!sessionId) return;

    setJoiningWaitingList(true);
    setError('');

    try {
      await axios.post(`/api/waiting-list/${sessionId}`);
              alert('Du er nu tilføjet til ventelisten! Du skal selv tjekke om du har fået en plads, på dagen for gusen.');
      navigate('/my-bookings');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Kunne ikke tilslutte dig ventelisten');
    } finally {
      setJoiningWaitingList(false);
    }
  };

  const canBook = () => {
    if (!monthlyBookings) return true;
    return monthlyBookings.count < monthlyBookings.limit;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Session ikke fundet</h2>
          <button
            onClick={() => navigate('/sessions')}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg"
          >
            Tilbage til Sessioner
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Gennemført!</h2>
          <p className="text-gray-600 mb-4">Din GUS session er blevet booket succesfuldt.</p>
          {monthlyBookings && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
              <p className="text-sm text-green-700">
                Månedlig booking status: {monthlyBookings.count} af {monthlyBookings.limit} sessioner booket
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500">Omdirigerer til dine bookinger...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Book GUS Session</h1>
        <p className="text-gray-600">Bekræft din booking for denne traditionelle danske sauna session.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Session Details */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Session Detaljer</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900">{session.name || 'GUS Session'}</h3>
              <p className="text-gray-600 text-sm">
                {session.description || 'Traditionel dansk sauna session med erfaren Gusmester'}
              </p>
            </div>
            
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Dato</span>
                </div>
                <span className="font-medium text-gray-900">
                  {format(new Date(session.session_date), 'EEEE, MMMM d, yyyy')}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>Tid</span>
                </div>
                <span className="font-medium text-gray-900">
                  {session.start_time} - {session.end_time}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  <span>Gusmester</span>
                </div>
                <span className="font-medium text-gray-900">
                  {session.session_type === 'special_event' && session.gusmesters && session.gusmesters.length > 0
                    ? formatGusmesterNames(session.gusmesters.map((g) => g.name))
                    : session.gusmester_name}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  <span>Kapacitet</span>
                </div>
                <span className="font-medium text-gray-900">
                  {session.booked_count}/{session.max_capacity}
                  {session.waiting_count > 0 && (
                    <span className="text-blue-700 text-sm ml-2">
                      ({session.waiting_count} på venteliste)
                    </span>
                  )}
                </span>
              </div>
            </div>

            {session.session_type === 'special_event' && session.gusmesters && session.gusmesters.length > 0 ? (
              <div className="text-sm text-gray-500">
                Kontakt: {session.gusmesters.map((g) => g.email).join(', ')}
              </div>
            ) : session.gusmester_email && (
              <div className="text-sm text-gray-500">
                Kontakt: {session.gusmester_email}
              </div>
            )}
          </div>
        </div>

        {/* Booking Confirmation */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Bekræft Booking</h2>
          
          <div className="space-y-6">
            {/* Monthly Booking Status */}
            {monthlyBookings && (
              <div className={`p-4 rounded-lg ${
                canBook() 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center">
                  {canBook() ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  )}
                  <div>
                    <h3 className={`font-medium ${
                      canBook() ? 'text-green-800' : 'text-red-800'
                    }`}>
                      Månedlig Booking Status
                    </h3>
                    <p className={`text-sm ${
                      canBook() ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {monthlyBookings.count} af {monthlyBookings.limit} sessioner booket denne måned
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Vigtig Information</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Sessioner afholdes alle dage kl. 20:00-21:00</li>
                <li>• Maksimum 16 personer per session</li>
                <li>• Grænse på 2 sessioner per måned per medlem</li>
                <li>• Sæson kører fra 1. september til 1. maj</li>
                <li>• Ankom venligst 10 minutter før sessionen starter</li>
              </ul>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
                {error.includes('ventelisten') && (
                  <button
                    onClick={handleJoinWaitingList}
                    disabled={joiningWaitingList}
                    className="mt-3 w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
                  >
                    {joiningWaitingList ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Tilslutter...
                      </div>
                    ) : (
                      'Ja, tilslut mig ventelisten'
                    )}
                  </button>
                )}
              </div>
            )}

            <button
              onClick={handleBooking}
              disabled={bookingLoading || !canBook()}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                bookingLoading || !canBook()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-700 hover:bg-blue-800 text-white'
              }`}
            >
              {bookingLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Booker...
                </div>
              ) : !canBook() ? (
                'Månedlig Grænse Nået'
              ) : (
                'Bekræft GUS Session Booking'
              )}
            </button>

            {!canBook() && monthlyBookings && (
              <p className="text-sm text-gray-500 text-center">
                Du har nået din månedlige grænse på {monthlyBookings.limit} session{monthlyBookings.limit === 1 ? '' : 'er'}. 
                Du kan booke igen næste måned.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking; 